import { get } from "lodash";
import moment from "moment-timezone";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { NavLink } from "react-router-dom";
import { Grid } from "semantic-ui-react";

import CopyString from "../../utils/CopyString.jsx";
import PCoIPString from "../../utils/PCoIPString.jsx";
import EntityLink from "../../utils/EntityLink.jsx";
import VPNDetailsSection from "../../vpns/VPNDetailsSection.jsx";
import LabelWithHelpPopup from "../../labelWithHelpPopup/LabelWithHelpPopup.jsx";
import OSAvatar from "../../utils/OSAvatar.jsx";

import { MODIFIED_AT_TIME_FORMAT } from "../../config.js";
import { AT_TYPES } from "../../../constants/entity.js";

import { selectCopy } from "../../../state/reducers/locale/copySelector.js";

class ResourceDetailsView extends PureComponent {
  static propTypes = {
    data: PropTypes.object,
    disabled: PropTypes.bool,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    disabled: false,
    copyKey: "resource.details_panel"
  };

  renderProjectLink = () => {
    const { data, disabled } = this.props;

    const project = data.project;

    if (!project || typeof project === "string") return null;

    if (disabled) return get(project, "name", "");

    return (
      <NavLink to={project["@id"]}>{`${get(project, "name", "")}`}</NavLink>
    );
  };

  renderVolumeDetails = () => {
    const {
      data: { volumeSize, rootVolumeSize, dataVolumeSize, dataVolumeCount },
      copyKey,
      copy
    } = this.props;

    return (
      <Fragment>
        {volumeSize && !rootVolumeSize && (
          <Fragment>
            <strong>{copy(`${copyKey}.volume_size`)}: </strong>
            {volumeSize}
            {copy(`${copyKey}.volume_units`)}
          </Fragment>
        )}
        {rootVolumeSize && (
          <Fragment>
            <br />
            <strong>{copy(`${copyKey}.root_volume_size`)}: </strong>
            {rootVolumeSize}
            {copy(`${copyKey}.volume_units`)}
          </Fragment>
        )}
        {dataVolumeSize && (
          <Fragment>
            <br />
            <strong>{copy(`${copyKey}.data_volume_size`)}: </strong>
            {dataVolumeCount && dataVolumeCount > 1 && `${dataVolumeCount} x `}
            {dataVolumeSize}
            {copy(`${copyKey}.volume_units`)}
          </Fragment>
        )}
      </Fragment>
    );
  };

  renderIPDetails = () => {
    const { data, copyKey, copy } = this.props;

    if (!data["publicIP"] && !data["privateIP"]) return null;

    return (
      <p>
        {data["publicIP"] && (
          <Fragment>
            <strong>{copy(`${copyKey}.public_ip`)}: </strong>

            {(data["@type"] === AT_TYPES.DISPLAY_GATEWAY ||
              data["@type"] === AT_TYPES.WORKSTATION) && (
              <PCoIPString publicIP={data["publicIP"]} />
            )}

            <CopyString
              copyText={data["publicIP"]}
              displayText={data["publicIP"]}
            />
            <br />
          </Fragment>
        )}
        {data["privateIP"] && (
          <Fragment>
            <strong>{copy(`${copyKey}.private_ip`)}: </strong>
            <CopyString
              copyText={data["privateIP"]}
              displayText={data["privateIP"]}
            />
          </Fragment>
        )}
      </p>
    );
  };

  render() {
    const { data, copyKey, copy } = this.props;

    if (!data) return null;

    return (
      <Grid stackable className="clear-both break-word">
        <Grid.Row columns={2}>
          <Grid.Column>
            <p>
              <strong>{copy(`${copyKey}.id`)}: </strong>
              {data.id && (
                <CopyString copyText={data.id} displayText={data.id} />
              )}
              <br />

              <strong>{copy(`${copyKey}.name`)}: </strong>
              <EntityLink entity={data} />

              {data.description && (
                <Fragment>
                  <br />
                  <strong>{`${copy(`${copyKey}.description`)}: `}</strong>
                  <span className="display-block">{`${data.description}`}</span>
                </Fragment>
              )}
              {data.createdAt && (
                <Fragment>
                  <br />
                  <strong>{`${copy(`${copyKey}.created_at`)}: `}</strong>
                  {moment(data.createdAt).format(MODIFIED_AT_TIME_FORMAT)}
                </Fragment>
              )}
              {data.updatedAt && (
                <Fragment>
                  <br />
                  <strong>{`${copy(`${copyKey}.last_updated`)}: `}</strong>
                  {moment(data.updatedAt).format(MODIFIED_AT_TIME_FORMAT)}
                </Fragment>
              )}
            </p>
            <p>
              {data.project && (
                <Fragment>
                  <strong>{copy(`${copyKey}.project`)}: </strong>
                  {this.renderProjectLink()}
                  <br />
                </Fragment>
              )}

              {data.provider && (
                <Fragment>
                  <strong>{copy(`${copyKey}.provider`)}: </strong>
                  {get(data, "provider.name", "")}
                  <br />
                </Fragment>
              )}

              {data.region && (
                <Fragment>
                  <strong>{copy(`${copyKey}.region`)}: </strong>
                  {get(data, "region.name", "")}
                  <br />
                </Fragment>
              )}

              {data.zone && (
                <Fragment>
                  <strong>{copy(`${copyKey}.zone`)}: </strong>
                  {get(data, "zone.name", "")}
                </Fragment>
              )}
            </p>
          </Grid.Column>
          <Grid.Column>
            <p>
              {data.image /*&& typeof(data.image) === "object" // <--- in case we want to hide this altogether in the future */ && (
                <Fragment>
                  <strong>{copy(`${copyKey}.image`)}: </strong>
                  <OSAvatar entity={data.image} />
                  <LabelWithHelpPopup
                    label={get(data, "image.name", "N.A.")}
                    help={get(data, "image.description", "")}
                  />
                  <br />
                </Fragment>
              )}
              {data.size && (
                <Fragment>
                  <strong>{copy(`${copyKey}.size`)}: </strong>
                  <LabelWithHelpPopup
                    label={get(data, "size.name", "")}
                    help={get(data, "size.description", "")}
                  />
                  <br />
                </Fragment>
              )}
              {this.renderVolumeDetails()}
            </p>

            {this.renderIPDetails()}

            {data["@type"] === AT_TYPES.VPN_GATEWAY && (
              <VPNDetailsSection {...this.props} />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

const mapStateToProps = state => ({
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(ResourceDetailsView);
